import Keycloak from "keycloak-js";
import { secureStorage } from "../utils/security";
import { withAcceptLanguageHeader } from "../utils/requestLanguage";

let globalLogoutHandler = null;

export const registerLogoutHandler = (handler) => {
  globalLogoutHandler = handler;
};

// Initialize Keycloak instance with secure configuration
const keycloakConfig = {
  url: window.env.REACT_APP_KEYCLOAK_URL,
  realm: window.env.REACT_APP_KEYCLOAK_REALM,
  clientId: window.env.REACT_APP_KEYCLOAK_CLIENT_ID,
};

// Remove trailing /auth if present (for compatibility with newer Keycloak versions)
if (keycloakConfig.url.endsWith("/auth")) {
  keycloakConfig.url = keycloakConfig.url.slice(0, -5);
}

const keycloak = new Keycloak(keycloakConfig);

// Add a flag to track initialization status
let keycloakInitialized = false;
let keycloakInitPromise = null;

const checkRefreshTokenExpiry = () => {
  if (!keycloak.refreshToken || !keycloak.refreshTokenParsed) {
    console.log("No refresh token available");
    return;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const refreshExp = keycloak.refreshTokenParsed.exp;

  if (refreshExp < currentTime) {
    console.log("❌ Keycloak refresh token has expired");
    // 👉 force logout
    doKeycloakLogout();
  }
};

const initKeycloak = (onAuthenticatedCallback) => {
  console.log("Initializing Keycloak...");

  const isLogoutCallback =
    window.location.pathname === "/logout-callback";

  // The logout redirect starts a fresh page load before LogoutCallback can
  // render. Remove the old tokens here so they cannot be used to restore the
  // session while Keycloak verifies that the server-side logout completed.
  if (isLogoutCallback) {
    secureStorage.clearAllTokens();
    return Promise.resolve(onAuthenticatedCallback?.());
  }

  if (keycloakInitialized) {
    onAuthenticatedCallback?.();
    return Promise.resolve();
  }

  if (keycloakInitPromise) {
    return keycloakInitPromise.then(onAuthenticatedCallback);
  }

  const initOptions = {
    onLoad: "check-sso",
    pkceMethod: "S256",
    checkLoginIframe: false,
  };

  keycloakInitPromise = keycloak
    .init(initOptions)
    .then(async (authenticated) => {
      keycloakInitialized = true;

      if (authenticated) {
        secureStorage.setToken(keycloak.token);
        if (keycloak.refreshToken)
          secureStorage.setRefreshToken(keycloak.refreshToken);
        if (keycloak.idToken) secureStorage.setIdToken(keycloak.idToken);
      } else if (secureStorage.getRefreshToken()) {
        try {
          await refreshKeycloakTokenWithStoredRefreshToken();
        } catch (refreshError) {
          console.warn("KEYCLOAK => stored refresh during init failed", refreshError);
          secureStorage.clearAllTokens();
        }
      } else {
        secureStorage.clearAllTokens();
      }

      onAuthenticatedCallback?.();

      setInterval(() => {
        checkRefreshTokenExpiry();
      }, 10000);
    })
    .catch(async (error) => {
      console.warn("KEYCLOAK => init failed", error);

      if (secureStorage.getRefreshToken()) {
        try {
          await refreshKeycloakTokenWithStoredRefreshToken();
        } catch (refreshError) {
          console.warn("KEYCLOAK => stored refresh after init failure failed", refreshError);
          secureStorage.clearAllTokens();
        }
      } else {
        secureStorage.clearAllTokens();
      }

      onAuthenticatedCallback?.();
    });

  return keycloakInitPromise;
};

// Token refresh is now handled by the centralized tokenManager

// Secure login functions with proper error handling
const doKeycloakLogin = async (targetRedirectPath = "/") => {
  await initKeycloak(); // 🔑 critical line

  console.log("Starting Keycloak login...");
  sessionStorage.setItem("redirectAfterLogin", targetRedirectPath);

  keycloak.login({
    redirectUri: window.location.origin + targetRedirectPath,
    prompt: "select_account", // always force fresh login screen
  });
};

// Secure social login functions
const doKeycloakGoogleLogin = (
  rememberMe = false,
  targetRedirectPath = null,
) => {
  try {
    if (targetRedirectPath) {
      sessionStorage.setItem("redirectAfterLogin", targetRedirectPath);
      // redirectAfterLogin set by targetRedirectPath
    } else if (
      window.location.pathname !== "/login" &&
      !sessionStorage.getItem("redirectAfterLogin")
    ) {
      sessionStorage.setItem("redirectAfterLogin", window.location.pathname);
      // redirectAfterLogin set by current pathname
    } else if (!sessionStorage.getItem("redirectAfterLogin")) {
      sessionStorage.setItem("redirectAfterLogin", "/");
      // redirectAfterLogin set to default: /
    }

    keycloak.login({
      redirectUri: window.location.origin,
      idpHint: "google",
      prompt: "login",
      rememberMe: rememberMe,
    });
  } catch (error) {
    // Error during Keycloak Google login
    secureStorage.clearAllTokens();
  }
};

const doKeycloakGithubLogin = (
  rememberMe = false,
  targetRedirectPath = null,
) => {
  try {
    if (targetRedirectPath) {
      sessionStorage.setItem("redirectAfterLogin", targetRedirectPath);
      // redirectAfterLogin set by targetRedirectPath
    } else if (
      window.location.pathname !== "/login" &&
      !sessionStorage.getItem("redirectAfterLogin")
    ) {
      sessionStorage.setItem("redirectAfterLogin", window.location.pathname);
      // redirectAfterLogin set by current pathname
    } else if (!sessionStorage.getItem("redirectAfterLogin")) {
      sessionStorage.setItem("redirectAfterLogin", "/");
      // redirectAfterLogin set to default: /
    }

    keycloak.login({
      redirectUri: window.location.origin,
      idpHint: "github",
      prompt: "login",
      rememberMe: rememberMe,
    });
  } catch (error) {
    // Error during Keycloak Github login
    secureStorage.clearAllTokens();
  }
};

// Secure logout function
const doKeycloakLogout = () => {
  try {
    console.log("Logging out... 1");
    const idToken = secureStorage.getIdToken() || keycloak.idToken;

    const redirectUri = window.location.origin + "/logout-callback";
    console.log("Logging out, redirecting to:", redirectUri);
    return keycloak.logout({
      redirectUri,
      idTokenHint: idToken,
    });
  } catch (error) {
    console.error("Logout error", error);
    keycloakInitialized = false;

    // fallback: force clear and go to callback
    secureStorage.clearAllTokens();
    window.location.href = "/logout-callback";
  }
};

// Helper function to parse JWT tokens
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    // Error parsing JWT token
    return {};
  }
};

const isJwtExpired = (token, bufferSeconds = 0) => {
  const parsed = parseJwt(token);
  if (!parsed.exp) return true;
  return parsed.exp <= Math.floor(Date.now() / 1000) + bufferSeconds;
};

// Secure token management
const getKeycloakToken = () => {
  try {
    return keycloak.token || secureStorage.getToken();
  } catch (error) {
    secureStorage.clearToken();
    return null;
  }
};

// Secure token refresh
const updateKeycloakToken = (successCallback) => {
  return keycloak
    .updateToken(60)
    .then(() => {
      try {
        secureStorage.setToken(keycloak.token);
        if (keycloak.refreshToken) {
          secureStorage.setRefreshToken(keycloak.refreshToken);
        }
        if (successCallback) successCallback();
      } catch (error) {
        console.error("KEYCLOAK => failed to persist refreshed token", error);
        throw error;
      }
    })
    .catch((error) => {
      console.warn("KEYCLOAK => updateToken failed", error);
      throw error;
    });
};

const refreshKeycloakTokenWithStoredRefreshToken = async () => {
  const refreshToken = secureStorage.getRefreshToken();

  if (!refreshToken) {
    const error = new Error("No refresh token available");
    error.code = "missing_refresh_token";
    throw error;
  }

  if (isJwtExpired(refreshToken)) {
    const error = new Error("Refresh token has expired");
    error.code = "refresh_token_expired";
    throw error;
  }

  const params = new URLSearchParams();
  params.append("client_id", keycloakConfig.clientId);
  params.append("grant_type", "refresh_token");
  params.append("refresh_token", refreshToken);

  const tokenUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`;

  console.debug("KEYCLOAK => refreshing access token with stored refresh token");

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: withAcceptLanguageHeader({
      "Content-Type": "application/x-www-form-urlencoded",
    }),
    body: params,
  });

  let tokenData = null;

  try {
    tokenData = await response.json();
  } catch {
    tokenData = null;
  }

  if (!response.ok) {
    const error = new Error(
      tokenData?.error_description || tokenData?.error || "Token refresh failed",
    );
    error.status = response.status;
    error.code = tokenData?.error;
    throw error;
  }

  if (!tokenData?.access_token) {
    const error = new Error("Refresh response did not include an access token");
    error.code = "missing_access_token";
    throw error;
  }

  secureStorage.setToken(tokenData.access_token);

  if (tokenData.refresh_token) {
    secureStorage.setRefreshToken(tokenData.refresh_token);
  }

  if (tokenData.id_token) {
    secureStorage.setIdToken(tokenData.id_token);
  }

  console.debug("KEYCLOAK => stored refreshed access token");

  return tokenData.access_token;
};

// Secure user info retrieval
const getKeycloakUserInfo = () => {
  try {
    const tokenParsed = keycloak.authenticated
      ? keycloak.tokenParsed
      : parseJwt(secureStorage.getToken());

    if (tokenParsed?.sub && !isJwtExpired(secureStorage.getToken() || keycloak.token)) {
      // Keycloak token parsed

      // Check for different role formats in the token
      let roles = [];

      // Check realm_access roles
      if (tokenParsed.realm_access?.roles) {
        // Found realm_access roles
        roles = [...tokenParsed.realm_access.roles];
      }

      // Check resource_access roles if available
      const clientId = keycloakConfig.clientId;
      if (tokenParsed.resource_access?.[clientId]?.roles) {
        // Found resource_access roles
        roles = [
          ...roles,
          ...tokenParsed.resource_access[clientId].roles,
        ];
      }

      // Check for groups if available
      if (tokenParsed.groups) {
        // Found groups
        // Convert groups to roles format (often groups are prefixed with "/")
        const groupRoles = tokenParsed.groups.map((group) =>
          group.startsWith("/") ? group.substring(1) : group,
        );
        roles = [...roles, ...groupRoles];
      }

      // Combined roles and groups

      return {
        id: keycloak.subject || tokenParsed.sub,
        username: tokenParsed.preferred_username,
        email: tokenParsed.email,
        firstName: tokenParsed.given_name,
        lastName: tokenParsed.family_name,
        roles: roles,
        groups: tokenParsed.groups || [],
        tokenParsed, // Include full token for debugging
      };
    }
    return null;
  } catch (error) {
    // Error getting user info
    secureStorage.clearToken();
    return null;
  }
};

// Secure authentication check
const isKeycloakAuthenticated = () => {
  try {
    const storedToken = secureStorage.getToken();
    return !!keycloak.authenticated || !!storedToken && !isJwtExpired(storedToken);
  } catch (error) {
    secureStorage.clearToken();
    return false;
  }
};

// Direct password authentication with Keycloak
const doKeycloakDirectLogin = async (
  username,
  password,
  rememberMe = false,
) => {
  try {
    const params = new URLSearchParams();
    params.append("client_id", keycloakConfig.clientId);
    params.append("username", username);
    params.append("password", password);
    params.append("grant_type", "password");

    const tokenUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`;

    // Attempting direct login...
    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: withAcceptLanguageHeader({
        "Content-Type": "application/x-www-form-urlencoded",
      }),
      body: params,
    });

    if (!response.ok) {
      const errorData = await response.json();
      // Direct login authentication error
      throw new Error(errorData.error_description || "Authentication failed");
    }

    const tokenData = await response.json();
    // Direct login successful. Received tokens. Server session established.

    // Store tokens securely (still potentially useful for refresh later)
    // Storing tokens locally.
    secureStorage.setToken(tokenData.access_token);

    if (tokenData.id_token) {
      secureStorage.setIdToken(tokenData.id_token);
    }

    if (tokenData.refresh_token) {
      secureStorage.setRefreshToken(tokenData.refresh_token);
    }

    // Indicate success (server-side session is set)
    return true;
  } catch (error) {
    // Keycloak direct login error
    secureStorage.clearAllTokens(); // Clear any potentially partially stored tokens on error
    throw error;
  }
};

export {
  keycloak,
  initKeycloak,
  doKeycloakLogin,
  doKeycloakGoogleLogin,
  doKeycloakGithubLogin,
  doKeycloakLogout,
  getKeycloakToken,
  updateKeycloakToken,
  refreshKeycloakTokenWithStoredRefreshToken,
  getKeycloakUserInfo,
  isKeycloakAuthenticated,
  doKeycloakDirectLogin,
  parseJwt,
};
